import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Home, Trophy, MapPin, UserRound, type LucideIcon } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { Avatar, getInitials, tintForId } from "@/ui";
import { PlaySheet } from "@/features/play/PlaySheet";
import { usePlayState } from "@/features/play/api";

// The tab bar only needs to know whether a search is running, so it polls
// rarely; open play screens poll faster on the same query.
const TABBAR_PLAY_REFETCH_MS = 60_000;

interface Tab {
  to: string;
  label: string;
  Icon: LucideIcon;
}

// «Сообщество» lives as a card on the home page; its slot went to the
// central «Играть» button, which sits between the two halves.
const TABS_LEFT: Tab[] = [
  { to: "/",          label: "Главная",     Icon: Home },
  { to: "/events",    label: "События",     Icon: Trophy },
];
const TABS_RIGHT: Tab[] = [
  { to: "/map",       label: "Карта",       Icon: MapPin },
  { to: "/profile",   label: "Профиль",     Icon: UserRound },
];

export function Shell() {
  const { user } = useAuth();
  const location = useLocation();

  // Hide tabbar on admin routes — they have their own layout.
  const hideTabbar = location.pathname.startsWith("/admin");

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "var(--bg)",
        paddingTop: "var(--safe-top)",
        // 56px bar + room for the raised «Играть» button above it.
        paddingBottom: hideTabbar ? 0 : "calc(56px + 24px + var(--safe-bottom))",
      }}
    >
      <main>
        <Outlet />
      </main>
      {!hideTabbar && <TabBar user={user} />}
    </div>
  );
}

function TabBar({ user }: { user: ReturnType<typeof useAuth>["user"] }) {
  const [playOpen, setPlayOpen] = useState(false);
  const playState = usePlayState({ enabled: !!user, refetchInterval: TABBAR_PLAY_REFETCH_MS });
  const searching = playState.data?.request?.status === "searching";

  const renderTab = ({ to, label, Icon }: Tab) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      style={({ isActive }) => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        color: isActive ? "var(--brand-orange)" : "var(--fg3)",
        textDecoration: "none",
        fontFamily: "var(--font-body)",
        fontSize: 11,
        fontWeight: 500,
        transition: "color var(--d-fast) var(--ease-out)",
      })}
    >
      {({ isActive }) => (
        <>
          {to === "/profile" && user ? (
            <Avatar
              initials={getInitials(user.first_name, user.last_name, user.username)}
              size={24}
              tint={tintForId(user.id)}
              src={user.photo_url}
            />
          ) : (
            <Icon size={22} strokeWidth={1.75} />
          )}
          <span style={{ opacity: isActive ? 1 : 0.85 }}>{label}</span>
        </>
      )}
    </NavLink>
  );

  return (
    <>
      <nav
        aria-label="Основная навигация"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          background: "var(--bg-tabbar)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTop: "1px solid var(--separator)",
          paddingBottom: "var(--safe-bottom)",
        }}
      >
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "stretch",
            height: 56,
          }}
        >
          {TABS_LEFT.map(renderTab)}
          <PlayButton searching={searching} onClick={() => setPlayOpen(true)} />
          {TABS_RIGHT.map(renderTab)}
        </div>
      </nav>
      {/* Outside <nav>: its backdrop-filter would trap the sheet's fixed overlay. */}
      <PlaySheet open={playOpen} onClose={() => setPlayOpen(false)} />
    </>
  );
}

/** Raised round CTA in the middle of the tab bar — opens the «Играть» sheet. */
function PlayButton({ searching, onClick }: { searching: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={searching ? "Играть — идёт поиск соперника" : "Играть"}
      style={{
        flex: 1,
        background: "transparent",
        border: 0,
        padding: 0,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 2,
        paddingBottom: 6,
        color: "var(--brand-orange)",
        fontFamily: "var(--font-body)",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <span
        className={searching ? "sp-play-btn sp-play-btn--searching" : "sp-play-btn"}
        style={{
          width: 52,
          height: 52,
          marginTop: -22,
          borderRadius: "50%",
          background: "var(--brand-orange)",
          color: "#fff",
          display: "grid",
          placeItems: "center",
          boxShadow: "0 4px 14px rgba(255, 90, 31, 0.35)",
          border: "3px solid var(--bg)",
        }}
      >
        <PaddleIcon />
      </span>
      Играть
    </button>
  );
}

function PaddleIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="9" r="6" />
      <path d="m14 13 6 7-3 2-6-7" />
      <circle cx="20" cy="5" r="2" />
    </svg>
  );
}
